import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { COLORS, TYPOGRAPHY } from "../designSystem";

const PRIMARY   = COLORS.PRIMARY;
const SECONDARY = COLORS.PRIMARY_DARK;
const DARK      = COLORS.GRADIENT_DARK;
const FONT      = TYPOGRAPHY.FONT_FAMILY_BODY;
const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify({
        id: response.data.user_id,
        email: response.data.email,
        tenant_id: response.data.tenant_id,
        role: response.data.role,
        is_owner: response.data.is_owner,
      }));

      navigate("/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Login failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#ffffff", display: "flex", fontFamily: FONT, overflow: "hidden" }}>

      {/* Left panel — branding */}
      <div style={{
        flex: 1,
        background: `linear-gradient(135deg, ${DARK} 0%, ${COLORS.GRADIENT_DARK_TINT} 25%, ${SECONDARY} 50%, ${COLORS.GRADIENT_MID} 75%, ${PRIMARY} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 48px",
        color: "white",
        position: "relative",
        overflow: "hidden",
        boxShadow: "inset -4px 0 16px rgba(0, 0, 0, 0.2)",
      }}>
        {/* Background elements */}
        <div style={{ position: "absolute", top: "-100px", right: "-100px", width: "300px", height: "300px", borderRadius: "50%", background: `rgba(77, 219, 148, 0.1)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-80px", left: "-60px", width: "280px", height: "280px", borderRadius: "50%", background: `rgba(77, 219, 148, 0.08)`, pointerEvents: "none" }} />

        <div style={{ maxWidth: "380px", textAlign: "center", position: "relative", zIndex: 1 }}>
          <img
            src="/assets/logo.png"
            alt="Emerald MCA"
            style={{ height: "56px", width: 'auto', marginBottom: "24px", filter: "brightness(0) invert(1)" }}
          />
          <h1 style={{ fontSize: "28px", fontWeight: "800", margin: "0 0 8px", letterSpacing: "-0.5px", color: "#fff" }}>
            Emerald MCA Solutions
          </h1>
          <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", margin: "0 0 40px", fontWeight: "500", letterSpacing: "0.3px" }}>by Finvance LLC</p>
          <p style={{ fontSize: "17px", color: "rgba(255,255,255,0.85)", lineHeight: 1.8, margin: "0 0 56px", fontWeight: "500", letterSpacing: '0.3px' }}>
            Manage your entire MCA business with confidence.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", textAlign: "left" }}>
            {[
              "Merchant & Deal Management",
              "State Compliance Automation",
              "Real-time Deal Tracking",
              "Enterprise Security"
            ].map(item => (
              <div key={item} style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "14px", fontWeight: "500", color: "rgba(255,255,255,0.9)" }}>
                <div style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(77, 219, 148, 0.2)",
                  border: `1.5px solid ${PRIMARY}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  flexShrink: 0,
                  color: PRIMARY,
                  fontWeight: "700",
                }}>
                  ✓
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 48px",
        backgroundColor: COLORS.BG_FORM,
        position: "relative",
      }}>
        <div style={{ width: "100%", maxWidth: "380px", backgroundColor: COLORS.BG_SURFACE_PRIMARY, padding: "48px 40px", borderRadius: "12px", boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)", border: `1px solid ${COLORS.BORDER_LIGHT}` }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <p style={{
              color: PRIMARY,
              fontSize: "13px",
              fontWeight: "700",
              margin: "0 0 48px",
              letterSpacing: "0.3px",
              cursor: "pointer",
              transition: "color 0.2s ease",
            }} onMouseEnter={(e) => e.currentTarget.style.color = SECONDARY} onMouseLeave={(e) => e.currentTarget.style.color = PRIMARY}>
              ← Back to home
            </p>
          </Link>

          <h2 style={{ fontSize: "28px", fontWeight: "800", color: DARK, margin: "0 0 8px", letterSpacing: "-0.5px" }}>Welcome back</h2>
          <p style={{ fontSize: "14px", color: "#888", margin: "0 0 40px", fontWeight: "400" }}>Sign in to your Emerald account</p>

          {error && (
            <div style={{
              padding: "12px 16px",
              backgroundColor: "#fee2e2",
              border: `1.5px solid #fca5a5`,
              borderRadius: "8px",
              color: "#991b1b",
              fontSize: "13px",
              marginBottom: "24px",
              fontWeight: "500",
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div>
              <label style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "700",
                color: DARK,
                marginBottom: "8px",
                fontFamily: FONT,
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                placeholder="you@example.com"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "15px",
                  fontFamily: FONT,
                  border: `1.5px solid ${emailFocused ? PRIMARY : "#e5e7eb"}`,
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s ease",
                  boxShadow: emailFocused ? `0 0 0 3px rgba(77, 219, 148, 0.12)` : "none",
                  backgroundColor: emailFocused ? "rgba(77, 219, 148, 0.02)" : "#fff",
                }}
              />
            </div>

            <div>
              <label style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "700",
                color: DARK,
                marginBottom: "8px",
                fontFamily: FONT,
              }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={passwordVisible ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPassFocused(true)}
                  onBlur={() => setPassFocused(false)}
                  placeholder="••••••••"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    paddingRight: "40px",
                    fontSize: "15px",
                    fontFamily: FONT,
                    border: `1.5px solid ${passFocused ? PRIMARY : "#e5e7eb"}`,
                    borderRadius: "8px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s ease",
                    boxShadow: passFocused ? `0 0 0 3px rgba(77, 219, 148, 0.12)` : "none",
                    backgroundColor: passFocused ? "rgba(77, 219, 148, 0.02)" : "#fff",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                    padding: "4px 8px",
                    color: "#999",
                  }}
                >
                  {passwordVisible ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px 16px",
                marginTop: "12px",
                backgroundColor: loading ? "#bbb" : PRIMARY,
                color: "#fff",
                border: "2px solid transparent",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "800",
                fontFamily: FONT,
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: `0 6px 20px rgba(77, 219, 148, 0.4)`,
                letterSpacing: '0.5px',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = `0 10px 32px rgba(77, 219, 148, 0.5)`;
                  e.currentTarget.style.backgroundColor = "#5eee9e";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 6px 20px rgba(77, 219, 148, 0.4)`;
                  e.currentTarget.style.backgroundColor = PRIMARY;
                }
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div style={{ marginTop: "32px", paddingTop: "24px", borderTop: "1px solid #e5e7eb", textAlign: "center" }}>
            <p style={{ fontSize: "13px", color: "#888", margin: "0 0 16px", fontFamily: FONT }}>
              Try with any email and password to explore.
            </p>
            <p style={{ fontSize: "13px", color: "#888", margin: 0, fontFamily: FONT }}>
              Don't have an account?{" "}
              <Link to="/signup" style={{ color: PRIMARY, textDecoration: "none", fontWeight: "600", cursor: "pointer" }}>
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
